import { useTranslation } from "react-i18next";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import {
  AssistantValue,
  TAssistantTool,
} from "../../../../../../../../services/api/instances/assistantsApi/types";
import { NormalizedStorageObject } from "../../../../../../../../services/api/instances/storageApi/types/object_types";
import { AppIconButton } from "../../../../../../../../shared/components/AppIconButton";
import { TDirection } from "../../../../../../types";
import { AppInput } from "../../../../../../../../shared/components/AppInput";
import { LongArrowIcon } from "../../../../../../../../shared/icons/LongArrowIcon";
import { TrashIcon } from "../../../../../../../../shared/icons/TrashIcon";
import { GenericAutocomplete } from "../../../../../../../../common/GenericAutocomplete";
import styles from "./styles.module.css";

interface Props {
  assistants: NormalizedStorageObject<AssistantValue>[];
  assistantTool: TAssistantTool;
  allTools: TAssistantTool[];
  assistant: NormalizedStorageObject<AssistantValue> | null;
  onSelectAssistant: (
    assistant: NormalizedStorageObject<AssistantValue> | null,
    id: string,
  ) => void;
  onDeleteAssistantTool: (id: string) => void;
  handleChangeInstruction: (instruction: string, id: string) => void;
  direction: TDirection;
}

export const AssistantTools = (props: Props) => {
  const [toolInstruction, setToolInstruction] = useState<string>("");
  const { t } = useTranslation();

  useEffect(() => {
    if (props.assistantTool) {
      setToolInstruction(props.assistantTool.when);
    }
  }, [props.assistantTool.id]);

  const handleChangeValue = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setToolInstruction(e.target.value);
  };

  const handleChange = useCallback(() => {
    props.handleChangeInstruction(toolInstruction, props.assistantTool.id);
  }, [props.handleChangeInstruction, toolInstruction, setToolInstruction]);

  const isToolFirst = props.direction === "left" || props.direction === "top";

  const filteredAssistants = props.assistants.filter((assistant) => {
    const isUsedByOtherTool = props.allTools.some((tool) => {
      if (tool.id === props.assistantTool.id) {
        return false;
      }
      return tool.ref === assistant.ref;
    });

    if (isUsedByOtherTool) {
      return false;
    }

    return true;
  });

  const instructionBlock = (
    <>
      <div
        className={classNames(
          styles["instruction-block-wrapper"],
          styles[`instruction-block-wrapper-${props.direction}`],
        )}
      >
        {isToolFirst ? (
          <>
            <LongArrowIcon />
            <AppInput
              value={toolInstruction}
              onChange={handleChangeValue}
              onBlur={handleChange}
              multiline
              rows={6}
              className={styles["instruction-input"]}
            />
          </>
        ) : (
          <>
            <AppInput
              value={props.assistantTool.when}
              onChange={handleChangeValue}
              onBlur={handleChange}
              multiline
              rows={6}
              className={styles["instruction-input"]}
            />
            <LongArrowIcon />
          </>
        )}
      </div>
    </>
  );

  const tool = (
    <div className={styles.wrapper}>
      <GenericAutocomplete<NormalizedStorageObject<AssistantValue>>
        options={filteredAssistants.filter((a) => a.value !== null)}
        value={props.assistant}
        onChange={(newAssistant) =>
          props.onSelectAssistant(newAssistant, props.assistantTool.id)
        }
        getLabel={(asst) => asst.friendly_name}
        getValue={(asst) => asst.id}
        getOptionKey={(asst) => asst.ref}
        autocompleteStyle={"select"}
      />
      <AppIconButton
        onClick={() =>
          props.onDeleteAssistantTool(props.assistantTool?.id ?? "")
        }
        className={styles["delete-btn"]}
      >
        <TrashIcon width={24} height={24} />
      </AppIconButton>
    </div>
  );

  return (
    <div
      className={classNames(
        styles["container"],
        styles[`assistant-badge-layout-${props.direction}`],
      )}
    >
      {isToolFirst ? (
        <>
          {tool}
          {instructionBlock}
        </>
      ) : (
        <>
          {instructionBlock}
          {tool}
        </>
      )}
    </div>
  );
};
